/**
 * Light sector arcs, computed from the cell LIGHTS features that carry sector
 * limits (S-57 SECTR1 / SECTR2). MapLibre can't draw an arc from a point via a
 * style expression, so we precompute the arc geometry here and feed it as its
 * own GeoJSON source, coloured by the sector's light colour.
 *
 * Honest note: the arc is drawn at a small fixed radius (a chart symbol, not the
 * light's true range), spanning the charted sector bearings around the light.
 * The bearings come straight from the cell; nothing is invented.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';
import { rhumbDestination } from '@/nav';
import type { ChartFeatureCollection } from '@/types';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'light-sectors';
/** Radius of the drawn sector arc (a symbol size, not the real range). */
const ARC_NM = 0.4;

function toNum(v: unknown): number | null {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

function sectorColour(letter: unknown): string {
  const c = String(letter ?? '').toUpperCase();
  if (c.includes('R')) return '#d11b2f';
  if (c.includes('G')) return '#0a8a2a';
  if (c.includes('Y') || c.includes('O')) return '#e8920a';
  return '#9aa0a6'; // a white sector, shown as a light grey arc so it's visible
}

function build(cells: ChartFeatureCollection[]): FeatureCollection {
  const features: Feature[] = [];
  for (const cell of cells) {
    for (const ft of cell.features) {
      if (ft.properties.objClass !== 'LIGHTS' || ft.geometry.type !== 'Point') continue;
      const raw = (ft.properties.attrs.raw ?? {}) as Record<string, unknown>;
      const s1 = toNum(raw.SECTR1);
      const s2 = toNum(raw.SECTR2);
      if (s1 === null || s2 === null) continue;
      const p = ft.geometry.coordinates as [number, number];
      const colour = sectorColour(ft.properties.attrs.colour);
      // Lit arc from SECTR1 to SECTR2, going clockwise.
      let span = s2 - s1;
      while (span <= 0) span += 360;
      while (span > 360) span -= 360;
      const steps = Math.max(2, Math.ceil(span / 2));
      const arc: [number, number][] = [];
      for (let i = 0; i <= steps; i++) {
        arc.push(rhumbDestination(p, s1 + (span * i) / steps, ARC_NM));
      }
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: arc },
        properties: { colour },
      });
      // Sector limit lines from the light out to each bearing.
      for (const b of [s1, s2]) {
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [p, rhumbDestination(p, b, ARC_NM)] },
          properties: { colour: '#555', limit: true },
        });
      }
    }
  }
  return { type: 'FeatureCollection', features };
}

export function ensureLightSectorsLayer(
  map: MaplibreMap,
  cells: ChartFeatureCollection[],
  palette: Palette,
): void {
  const fc = build(cells);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: fc });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
  }
  if (!map.getLayer('light-sector-limit')) {
    map.addLayer({
      id: 'light-sector-limit',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'limit'], true],
      minzoom: 11,
      paint: {
        'line-color': getPalette(palette).CHBLK,
        'line-width': 0.7,
        'line-dasharray': [2, 2],
        'line-opacity': 0.55,
      },
    });
  }
  if (!map.getLayer('light-sector-arc')) {
    map.addLayer({
      id: 'light-sector-arc',
      type: 'line',
      source: SOURCE_ID,
      filter: ['!=', ['get', 'limit'], true],
      minzoom: 10,
      paint: {
        'line-color': ['get', 'colour'],
        'line-width': 2.6,
        'line-opacity': 0.9,
      },
    });
  }
}
